import { SurfaceWidget } from './SurfaceWidget';
import * as React from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router';
import { TreeListItem } from './TreeListWidget';
import { DOCS_PAGES, normalizePermalink } from '../pages/docs/shared/docs';
import { PHONE_MEDIA } from '../vars';

export const docsTopicSectionId = (label: string) => `topic-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

const descriptions = new Map(
  DOCS_PAGES.map((page) => [normalizePermalink(page.frontmatter.permalink), page.frontmatter.description])
);

interface TopicGroup {
  key: string;
  title: string;
  trail: string[];
  items: TreeListItem[];
}

/**
 * Give each subgroup its own block instead of reserving a column for an entire branch.
 */
const collectGroups = (item: TreeListItem, trail: string[] = []): TopicGroup[] => {
  const children = item.children ?? [];
  const pages = children.filter((child) => !child.children?.length);
  return [
    ...(pages.length ? [{ key: [...trail, item.key].join('/'), title: item.label, trail, items: pages }] : []),
    ...children
      .filter((child) => child.children?.length)
      .flatMap((child) => collectGroups(child, [...trail, item.label]))
  ];
};

const Topic: React.FC<{ item: TreeListItem }> = ({ item }) => {
  if (!item.to) return null;
  const description = descriptions.get(normalizePermalink(item.to));
  return (
    <S.Entry>
      <Link to={item.to}>{item.label}</Link>
      {description && <p>{description}</p>}
    </S.Entry>
  );
};

export interface DocsTopicSectionWidgetProps {
  section: TreeListItem;
  title?: string;
  description?: string;
  browseLinks?: { label: string; to: string }[];
  collapsible?: boolean;
}

export const DocsTopicSectionWidget: React.FC<DocsTopicSectionWidgetProps> = ({
  section,
  title,
  description,
  browseLinks,
  collapsible = false
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const contentId = React.useId();
  const children = section.children ?? [section];
  const overview = children.find((item) => item.label === 'Overview' && !item.children?.length);
  const remaining = children.filter((item) => item !== overview);
  const pages = remaining.filter((item) => !item.children?.length);
  const groups = remaining.filter((item) => item.children?.length).flatMap((item) => collectGroups(item));
  const hasMore = pages.length > 6 || groups.length > 3 || groups.some((group) => group.items.length > 3);
  const preview = collapsible && !expanded;
  const visiblePages = preview ? pages.slice(0, 6) : pages;
  const visibleGroups = preview ? groups.slice(0, 3) : groups;
  return (
    <S.Section as="section" id={docsTopicSectionId(section.label)}>
      <h2>{title ?? section.label}</h2>
      {description && <S.Description>{description}</S.Description>}
      {overview && (
        <S.Overview>
          <Topic item={overview} />
        </S.Overview>
      )}
      <S.Columns id={contentId}>
        {visiblePages.map((item) => (
          <S.Group key={item.key}>
            <Topic item={item} />
          </S.Group>
        ))}
        {visibleGroups.map((group) => (
          <S.Group key={group.key}>
            {group.trail.length > 0 && <S.Trail>{group.trail.join(' / ')}</S.Trail>}
            <h3>{group.title}</h3>
            <S.List>
              {(preview ? group.items.slice(0, 3) : group.items).map((item) => (
                <li key={item.key}>
                  <Topic item={item} />
                </li>
              ))}
            </S.List>
          </S.Group>
        ))}
      </S.Columns>
      {((collapsible && hasMore) || browseLinks?.length) && (
        <S.Actions>
          {collapsible && hasMore && (
            <S.Expand
              type="button"
              aria-expanded={expanded}
              aria-controls={contentId}
              onClick={() => setExpanded((value) => !value)}
            >
              {expanded ? 'Show less' : 'Show more'}
            </S.Expand>
          )}
          {browseLinks?.map((link) => (
            <S.Browse key={link.to} to={link.to}>
              {link.label} <span aria-hidden="true">→</span>
            </S.Browse>
          ))}
        </S.Actions>
      )}
    </S.Section>
  );
};

namespace S {
  export const Section = styled(SurfaceWidget)`
    box-sizing: border-box;
    scroll-margin-top: 90px;
    padding: 32px;
    margin: 24px 0;
    h2 {
      color: ${(p) => p.theme.text.heading};
      margin: 0 0 24px;
    }
    @media (max-width: ${PHONE_MEDIA}px) {
      padding: 20px;
    }
  `;
  export const Overview = styled.div`
    margin-bottom: 28px;
    max-width: 720px;
  `;
  export const Columns = styled.div`
    columns: 3 300px;
    column-gap: 32px;
  `;
  export const Trail = styled.div`
    color: ${(p) => p.theme.text.paragraph};
    font-size: 12px;
    line-height: 1.5;
    margin-bottom: 6px;
  `;
  export const Group = styled.div`
    min-width: 0;
    break-inside: avoid;
    margin-bottom: 28px;
    display: flow-root;
    h3 {
      color: ${(p) => p.theme.text.heading};
      font-size: 16px;
      margin: 0 0 12px;
    }
  `;
  export const List = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    > li {
      margin: 14px 0;
    }
    ul {
      padding-left: 12px;
      border-left: 1px solid ${(p) => p.theme.surface.border};
    }
  `;
  export const Entry = styled.div`
    min-width: 0;
    overflow-wrap: anywhere;
    a {
      color: ${(p) => p.theme.text.special};
      font-weight: 500;
      text-decoration: none;
      &:hover {
        text-decoration: underline;
      }
    }
    p {
      color: ${(p) => p.theme.text.paragraph};
      font-size: 13px;
      line-height: 1.5;
      margin: 6px 0 0;
    }
  `;
  export const Description = styled.p`
    color: ${(p) => p.theme.text.paragraph};
    line-height: 1.6;
    margin: -10px 0 24px;
  `;
  export const Actions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 32px;
  `;
  export const Browse = styled(Link)`
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 11px 16px;
    border: 1px solid ${(p) => p.theme.text.special};
    border-radius: 10px;
    color: ${(p) => p.theme.text.special};
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    &:hover {
      transition: background 160ms ease;
      background: ${(p) => p.theme.card.hoverBackground};
      text-decoration: underline;
    }
    &:focus-visible {
      outline: 2px solid ${(p) => p.theme.text.special};
      outline-offset: 3px;
    }
  `;
  export const Expand = styled.button`
    padding: 11px 16px;
    border: 1px solid ${(p) => p.theme.text.special};
    border-radius: 10px;
    background: transparent;
    color: ${(p) => p.theme.text.special};
    font: inherit;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    &:hover {
      transition: background 160ms ease;
      background: ${(p) => p.theme.card.hoverBackground};
    }
    &:focus-visible {
      outline: 2px solid ${(p) => p.theme.text.special};
      outline-offset: 3px;
    }
  `;
}
